import {
  PORTFOLIO_RANGES,
  type PortfolioPoint,
  type PortfolioRange,
  type PortfolioSeries,
} from "@auto-pools/core";

/**
 * The portfolio chart's pure parts (design 59). Every figure is a server USD decimal string (USDC
 * and USDT count 1:1) or a server percent string; `Number()` appears here only to turn those
 * strings into plot coordinates for lightweight-charts — nothing printed is computed in the
 * browser, and a null figure stays a gap, never a zero.
 */

export type ChartMode = "result" | "value";
export type ChartUnit = "usd" | "percent";
export type ResultSeries = "total" | "pools" | "spot";
export type ValueSeries = "pools" | "spot" | "freeCash";

export interface ChartPreferences {
  open: boolean;
  range: PortfolioRange;
  mode: ChartMode;
  /** The unit the user chose; Value mode shows $ without overwriting it. */
  unit: ChartUnit;
  /** Checked series in Result mode (Free cash has no result, so it is never one of them). */
  result: ResultSeries[];
  /** Checked layers in Value mode. */
  value: ValueSeries[];
}

export const PREFERENCES_KEY = "auto-pools.portfolio-chart";
export const CHART_MODES: readonly ChartMode[] = ["result", "value"];
export const CHART_UNITS: readonly ChartUnit[] = ["usd", "percent"];
export const RESULT_SERIES: readonly ResultSeries[] = ["total", "pools", "spot"];
export const VALUE_SERIES: readonly ValueSeries[] = ["pools", "spot", "freeCash"];
export const DEFAULT_PREFERENCES: ChartPreferences = {
  open: false,
  range: "all",
  mode: "result",
  unit: "usd",
  result: ["total", "pools", "spot"],
  value: ["pools", "spot", "freeCash"],
};

function seriesList<T extends string>(value: unknown, allowed: readonly T[]): T[] | null {
  if (!Array.isArray(value)) return null;
  const items: unknown[] = value;
  if (!items.every((item): item is T => allowed.includes(item as T))) return null;
  if (new Set(items).size !== items.length) return null;
  // Keep the canonical order, whatever order was stored.
  return allowed.filter((key) => items.includes(key));
}

/** Each field is validated on its own; anything malformed falls back to its default. */
export function parsePreferences(raw: string | null): ChartPreferences {
  if (raw === null) return { ...DEFAULT_PREFERENCES };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
    return { ...DEFAULT_PREFERENCES };
  const stored = parsed as Record<string, unknown>;
  return {
    open: typeof stored.open === "boolean" ? stored.open : DEFAULT_PREFERENCES.open,
    range: PORTFOLIO_RANGES.includes(stored.range as PortfolioRange)
      ? (stored.range as PortfolioRange)
      : DEFAULT_PREFERENCES.range,
    mode: CHART_MODES.includes(stored.mode as ChartMode)
      ? (stored.mode as ChartMode)
      : DEFAULT_PREFERENCES.mode,
    unit: CHART_UNITS.includes(stored.unit as ChartUnit)
      ? (stored.unit as ChartUnit)
      : DEFAULT_PREFERENCES.unit,
    result: seriesList(stored.result, RESULT_SERIES) ?? [...DEFAULT_PREFERENCES.result],
    value: seriesList(stored.value, VALUE_SERIES) ?? [...DEFAULT_PREFERENCES.value],
  };
}

export function readPreferences(): ChartPreferences {
  try {
    return parsePreferences(window.localStorage.getItem(PREFERENCES_KEY));
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

export function writePreferences(preferences: ChartPreferences): void {
  try {
    window.localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
  } catch {
    // Storage may be unavailable (private mode, quota); the choice then lasts this visit only.
  }
}

/** The selected range when it has points, else the widest one that does; null when none has. */
export function effectiveRange(
  selected: PortfolioRange,
  available: Record<PortfolioRange, boolean>,
): PortfolioRange | null {
  if (available[selected]) return selected;
  return [...PORTFOLIO_RANGES].reverse().find((range) => available[range]) ?? null;
}

/** lightweight-charts time: whole UTC seconds. */
export function plotTime(at: string): number {
  return Math.floor(Date.parse(at) / 1000);
}

/** A server decimal string as a plot coordinate; null (and anything unparsable) is no point. */
function coordinate(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export type PlotRow<S> = { time: number } | ({ time: number; value: number } & Partial<S>);

/**
 * One series' rows. A null figure or a gap point is whitespace. lightweight-charts drops whitespace
 * and joins the neighbours, drawing the segment from a point to the next drawn one with the first
 * point's style — so the last known point before missing time (when a later known point exists)
 * carries `hidden` (transparent colours) and the bridge across the missing interval is not drawn.
 */
export function plotRows<S extends object>(
  points: readonly PortfolioPoint[],
  pick: (point: PortfolioPoint) => string | null | undefined,
  hidden: S,
): PlotRow<S>[] {
  const values = points.map((point) => (point.gap ? null : coordinate(pick(point))));
  return points.map((point, index) => {
    const time = plotTime(point.at);
    const value = values[index] ?? null;
    if (value === null) return { time };
    const next = values[index + 1] ?? null;
    const resumesLater =
      next === null && values.slice(index + 1).some((candidate) => candidate !== null);
    return resumesLater ? { time, value, ...hidden } : { time, value };
  });
}

export function resultFigure(
  point: PortfolioPoint,
  series: ResultSeries,
  unit: ChartUnit,
): string | null {
  const result = point.result?.[series] ?? null;
  if (!result) return null;
  return unit === "usd" ? result.result : result.percent;
}

/** The stack edge Value mode draws for a layer: pools, pools+spot, total (server sums). */
export function valueEdge(point: PortfolioPoint, layer: ValueSeries): string | null {
  const stack = point.value?.stack;
  if (!stack) return null;
  return layer === "pools" ? stack.pools : layer === "spot" ? stack.poolsSpot : stack.total;
}

export interface ChipState {
  key: ResultSeries | ValueSeries | "freeCash";
  label: string;
  /** Why the chip cannot be checked; null when it can. */
  disabledReason: string | null;
}

const RESULT_NAMES: Record<ResultSeries, string> = { total: "Total", pools: "Pools", spot: "Spot" };
const VALUE_NAMES: Record<ValueSeries, string> = {
  pools: "In pools",
  spot: "In spot",
  freeCash: "Free cash",
};

/** Whether the points carry any spot figure (a result or a value). */
export function hasSpotData(points: readonly PortfolioPoint[]): boolean {
  return points.some(
    (point) =>
      (point.result?.spot?.result ?? null) !== null || (point.value?.spot ?? null) !== null,
  );
}

/**
 * The chips of a mode, in order, each with its disabled reason: a series whose points have no
 * figure in this mode/unit says `<Name> · no history yet` instead of drawing a zero line. Free cash
 * has no result, so in Result mode it says `Free cash · Value only`. Spot is left out entirely when
 * there is no spot leg and the points carry no spot data.
 */
export function chipStates(
  points: readonly PortfolioPoint[],
  mode: ChartMode,
  unit: ChartUnit,
  spotLeg: boolean,
): ChipState[] {
  const showSpot = spotLeg || hasSpotData(points);
  const noHistory = (name: string, pick: (point: PortfolioPoint) => string | null) =>
    points.some((point) => !point.gap && pick(point) !== null) ? null : `${name} · no history yet`;
  if (mode === "result") {
    const chips: ChipState[] = RESULT_SERIES.filter((key) => key !== "spot" || showSpot).map(
      (key) => ({
        key,
        label: RESULT_NAMES[key],
        disabledReason: noHistory(RESULT_NAMES[key], (point) => resultFigure(point, key, unit)),
      }),
    );
    return [
      ...chips,
      { key: "freeCash", label: "Free cash", disabledReason: "Free cash · Value only" },
    ];
  }
  // A layer has history only where its OWN figure is known as well as its edge: the spot edge
  // (pools + spot) equals pools while spot is not counted, which is no spot history.
  return VALUE_SERIES.filter((key) => key !== "spot" || showSpot).map((key) => ({
    key,
    label: VALUE_NAMES[key],
    disabledReason: noHistory(VALUE_NAMES[key], (point) =>
      (point.value?.[key] ?? null) === null ? null : valueEdge(point, key),
    ),
  }));
}

const DAY_MS = 86_400_000;
const RANGE_MS: Record<Exclude<PortfolioRange, "all">, number> = {
  "1d": DAY_MS,
  "1w": 7 * DAY_MS,
  "1m": 30 * DAY_MS,
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "26 Oct" for a UTC day (YYYY-MM-DD) — the day the server's day row belongs to. */
export function formatRecordedDay(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return day;
  return `${String(Number(match[3]))} ${MONTHS[Number(match[2]) - 1] ?? match[2]}`;
}

/** "8 Oct" in local time. */
export function formatPlotDate(at: string): string {
  const date = new Date(at);
  return `${String(date.getDate())} ${MONTHS[date.getMonth()] ?? ""}`;
}

/** "8 Oct, 14:00" in local time: the exact moment a hovered figure belongs to. */
export function formatPointTime(at: string): string {
  const date = new Date(at);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${formatPlotDate(at)}, ${hours}:${minutes}`;
}

/**
 * `· since 26 Oct` when the record starts after the range's own start (always on ALL, which by
 * definition starts at the first recorded day); null when the record covers the whole range.
 */
export function sinceSuffix(
  firstRecordedDay: string | null,
  range: PortfolioRange,
  now: number,
): string | null {
  if (firstRecordedDay === null) return null;
  const since = ` · since ${formatRecordedDay(firstRecordedDay)}`;
  if (range === "all") return since;
  const first = Date.parse(`${firstRecordedDay}T00:00:00.000Z`);
  if (!Number.isFinite(first)) return null;
  return first > now - RANGE_MS[range] ? since : null;
}

/** The header label while nothing is hovered. */
export function intervalLabel(
  mode: ChartMode,
  firstRecordedDay: string | null,
  range: PortfolioRange,
  now: number,
): string {
  const base = mode === "result" ? "Result · all-time" : "Value · today";
  return base + (sinceSuffix(firstRecordedDay, range, now) ?? "");
}

/** The header label for a hovered point: the exact local time of the figures shown. */
export function hoverLabel(mode: ChartMode, point: PortfolioPoint): string {
  if (mode === "result")
    return `Result · ${formatPointTime(point.at)}${point.reconstructed ? " · rebuilt" : ""}`;
  // Value figures are dated by their OLDEST observation, never later than they were observed.
  return `Value · ${formatPointTime(point.value?.at ?? point.at)}`;
}

/** Whether the record is younger than 30 days, so 1M and ALL hold the same points. */
export function youngRecord(
  series: Pick<PortfolioSeries, "firstRecordedDay">,
  now: number,
): boolean {
  if (series.firstRecordedDay === null) return false;
  const first = Date.parse(`${series.firstRecordedDay}T00:00:00.000Z`);
  return Number.isFinite(first) && first > now - RANGE_MS["1m"];
}

/**
 * What the server rebuilt from stored history (`PortfolioPoint.reconstructed`) and where the
 * live record starts, as the note's text; null when no point in the range was rebuilt. Dates
 * come from the points' own times, nothing is computed.
 */
/** "26 Oct" of the point's UTC day: rebuilt day points sit at their UTC day's end, which a local
 * date would push into the next day east of UTC. */
const utcDay = (at: string) => {
  const date = new Date(at);
  return `${String(date.getUTCDate())} ${MONTHS[date.getUTCMonth()] ?? ""}`;
};

function rebuiltNote(mode: ChartMode, points: readonly PortfolioPoint[]): string | null {
  const real = points.filter((point) => !point.gap);
  if (!real.some((point) => point.reconstructed)) return null;
  const live = real.find((point) => !point.reconstructed);
  const mark = real.find((point) => point.reconstructed?.pools === "mark");
  const from = live ? utcDay(live.at) : null;
  if (mode === "value")
    return from === null
      ? "Value is not rebuilt for past days"
      : `Value is recorded from ${from} · percent is off in this mode`;
  const before = from === null ? "History" : `Before ${from}`;
  const detail = mark && mark !== real[0] ? `, realized only before ${utcDay(mark.at)}` : "";
  return `${before} rebuilt from the journal${detail}`;
}

/**
 * The note at the chips' right. A young record (`youngRecord`) means 1M and ALL hold the same
 * points, which the note says outright; otherwise Result explains the dashed line and Value why
 * percent is off.
 */
export function chartNote(
  mode: ChartMode,
  series: Pick<PortfolioSeries, "firstRecordedDay" | "daysRecorded">,
  now: number,
  points: readonly PortfolioPoint[] = [],
): string {
  const rebuilt = rebuiltNote(mode, points);
  if (rebuilt !== null) return rebuilt;
  if (youngRecord(series, now)) {
    const days = series.daysRecorded;
    return `${String(days)} ${days === 1 ? "day" : "days"} recorded · 1M and ALL show the same points until there are more`;
  }
  return mode === "result"
    ? "dashed line = break-even"
    : "stacked, so the top edge is the Total · percent is off in this mode";
}
